import { getCsrfToken } from "@/api/messages";

export type FileLibraryChatMessage = {
  id: number;
  role: "user" | "assistant";
  content: string;
  status: string;
  created_at: string;
  meta_data?: Record<string, unknown>;
};

export type FileLibraryDocumentChatBlockedCode =
  | "missing_file"
  | "not_file_library"
  | "not_in_municipality"
  | "unsupported_file_type"
  | "page_count_over_limit"
  | "page_count_unknown";

export type FileLibraryConversationPayload = {
  conversation_id: number;
  document_id: number;
  municipality_id: number;
  document_chat_blocked_reason: string | null;
  document_chat_blocked_code: FileLibraryDocumentChatBlockedCode | null;
  document_text_status: string | null;
  document_text_ready: boolean;
  document_text_failure_reason: string | null;
  document_text_truncated: boolean;
  messages: FileLibraryChatMessage[];
};

export type PostMessageResult =
  | { status: "pending"; user_message_id: number; assistant_message_id: number }
  | { status: "completed"; user_message_id: number; assistant_message_id: number; reply: string };

export type PollMessageResult = {
  id: number;
  role: string;
  content: string;
  status: string;
  created_at: string;
};

export const FILE_LIBRARY_CHAT_POLL_INTERVAL_MS = 2_000;

/** Scale poll window for PDF prep (pdftotext) and Reducto OCR on scanned files. */
export function fileLibraryChatPollTimeoutMs(pageCount?: number | null): number {
  const pages = Math.max(0, pageCount ?? 0);
  return Math.min(300_000, Math.max(180_000, 60_000 + pages * 2_000));
}

function conversationBase(municipalityId: number, documentId: number) {
  return `/api/v1/municipalities/${municipalityId}/file_library/documents/${documentId}/conversation`;
}

async function parseJson<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const text = await response.text();
    let message = text || `Request failed (${response.status})`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      // keep raw text
    }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export async function getFileLibraryConversation(
  municipalityId: number,
  documentId: number,
): Promise<FileLibraryConversationPayload> {
  const response = await fetch(conversationBase(municipalityId, documentId), {
    method: "GET",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    credentials: "include",
  });
  return parseJson(response);
}

export async function postFileLibraryDocumentMessage(
  municipalityId: number,
  documentId: number,
  content: string,
): Promise<PostMessageResult> {
  const response = await fetch(`${conversationBase(municipalityId, documentId)}/messages`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": getCsrfToken(),
    },
    credentials: "include",
    body: JSON.stringify({ message: { content } }),
  });
  return parseJson(response);
}

export async function getFileLibraryDocumentMessage(
  municipalityId: number,
  documentId: number,
  messageId: number,
): Promise<PollMessageResult> {
  const response = await fetch(`${conversationBase(municipalityId, documentId)}/messages/${messageId}`, {
    method: "GET",
    headers: { Accept: "application/json", "X-CSRF-Token": getCsrfToken() },
    credentials: "include",
  });
  return parseJson(response);
}

export function fileLibraryDocumentChatEnabled(document: {
  document_chat_blocked_reason?: string | null;
  document_chat_blocked_code?: FileLibraryDocumentChatBlockedCode | null;
}): boolean {
  return !document.document_chat_blocked_reason && !document.document_chat_blocked_code;
}

export function fileLibraryDocumentBetterForResearchAssistant(document: {
  document_chat_blocked_reason?: string | null;
  document_chat_blocked_code?: FileLibraryDocumentChatBlockedCode | null;
}): boolean {
  return document.document_chat_blocked_code === "page_count_over_limit";
}

export function fileLibraryDocumentChatUnavailableMessage(document: {
  document_chat_blocked_reason?: string | null;
  document_chat_blocked_code?: FileLibraryDocumentChatBlockedCode | null;
}): string | null {
  const reason = document.document_chat_blocked_reason;
  if (!reason && !document.document_chat_blocked_code) return null;

  if (fileLibraryDocumentBetterForResearchAssistant(document)) {
    return "This document is too large for File Center chat. Use Research Assistant for larger document research, or ask your Ordinal admin to add it there.";
  }

  return reason ?? "Chat is unavailable for this document.";
}

export const FILE_LIBRARY_DOCUMENT_CHAT_LABEL = "Chat about this file";
